import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  AppBar,
  Toolbar,
  Card,
  CardContent,
} from '@mui/material';
import { Favorite, Chat, Security, AutoAwesome } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const MainPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) {
    return null;
  }

  const features = [
    {
      icon: <Security fontSize="large" />,
      title: '안전한 만남',
      description: '철저한 본인 인증과 관리자 승인을 통한 안전한 커뮤니티',
    },
    {
      icon: <Favorite fontSize="large" />,
      title: '큐피트 카드',
      description: '매주 새로운 인연을 만날 수 있는 특별한 카드 시스템',
    },
    {
      icon: <AutoAwesome fontSize="large" />,
      title: 'AI 맞춤 매칭',
      description: 'AI가 취향과 성향을 분석하여 최적의 인연을 추천합니다',
    },
    {
      icon: <Chat fontSize="large" />,
      title: '자연스러운 대화',
      description: '부담 없이 시작하는 진정한 소통',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F5F7FA' }}>
      {/* AppBar — 네이비 */}
      <AppBar position="static" elevation={0}
        sx={{
          bgcolor: '#0D1F3C',
          borderRadius: 0,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute', bottom: 0, left: 0, right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, #C9A86C, transparent 70%)',
          },
        }}>
        <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, px: { xs: 2, sm: 3 } }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2px' }}>
            <Typography variant="h6" component="div" sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.375rem' },
              color: 'white',
              lineHeight: 1.2,
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              Silver<Box component="span" sx={{ color: '#C9A86C' }}>Connect</Box>
            </Typography>
            <Typography sx={{
              fontSize: { xs: '10px', sm: '11px' },
              letterSpacing: '1.8px',
              textTransform: 'uppercase',
              color: 'rgba(201,168,108,0.60)',
              fontWeight: 500,
              lineHeight: 1,
            }}>
              시니어 프리미엄 매칭
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/login')}
            sx={{
              color: '#E8CFA0',
              borderColor: 'rgba(201,168,108,0.4)',
              borderRadius: 20,
              px: 3,
              minHeight: 44,
              mr: 1,
              '&:hover': {
                borderColor: '#C9A86C',
                bgcolor: 'rgba(201,168,108,0.08)',
              }
            }}
          >
            로그인
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate('/register')}
            sx={{
              color: '#0D1F3C',
              borderRadius: 20,
              px: 3,
              minHeight: 44,
              fontWeight: 700,
              '&:hover': { bgcolor: '#B09050' }
            }}
          >
            회원가입
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section — 다크 네이비 그라디언트 */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0D1F3C 0%, #1A3360 55%, #283C6E 100%)',
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 10, md: 16 },
        textAlign: 'center',
      }}>
        {/* 장식 링 */}
        <Box sx={{
          position: 'absolute', top: -60, right: -60,
          width: 300, height: 300, borderRadius: '50%',
          border: '1.5px solid rgba(201,168,108,0.15)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -40, left: -40,
          width: 200, height: 200, borderRadius: '50%',
          border: '1px solid rgba(201,168,108,0.1)',
          pointerEvents: 'none',
        }} />

        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Typography sx={{
            fontSize: '11px', fontWeight: 600,
            letterSpacing: '2.5px', textTransform: 'uppercase',
            color: '#C9A86C', mb: 2, opacity: 0.9,
          }}>
            Premium Senior Matching
          </Typography>
          <Typography variant="h2" sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            fontSize: { xs: '1.875rem', md: '3rem' },
            color: '#FFFFFF',
            mb: 2,
            lineHeight: 1.2,
          }}>
            품격 있는 만남을<br />
            <Box component="em" sx={{ fontStyle: 'italic', color: '#E8CFA0' }}>
              당신에게
            </Box>{' '}드립니다
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, color: 'rgba(255,255,255,0.65)', mb: 5, lineHeight: 1.7 }}>
            시니어를 위한 프리미엄 매칭 서비스
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              color: '#0D1F3C',
              px: 6,
              py: 1.5,
              fontSize: '1.125rem',
              borderRadius: 28,
              minHeight: 56,
              fontWeight: 700,
              '&:hover': { bgcolor: '#B09050' }
            }}
          >
            지금 시작하기 →
          </Button>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* 빈 spacer — 이전 Hero Box 제거 후 구조 유지 */}
        <Box sx={{ pt: 0 }}>

        {/* Features Section */}
        <Box sx={{ py: 6 }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: '#0D1F3C',
              mb: 1
            }}>
              Silver Connect만의 특별함
            </Typography>
            <Box sx={{ width: 80, height: 2, bgcolor: '#C9A86C', mx: 'auto' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
            {features.map((feature, index) => (
              <Card key={index} sx={{
                flex: '1 1 250px',
                minWidth: 250,
                maxWidth: 300,
                bgcolor: '#FFFFFF',
                border: '1px solid #E4E1D9',
                borderRadius: 3,
                boxShadow: '0 4px 24px rgba(13,31,60,0.08)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(13,31,60,0.14)'
                }
              }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Box sx={{ color: '#0D1F3C', mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 700,
                    color: '#0D1F3C',
                    mb: 1
                  }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ color: '#6B7A8D', lineHeight: 1.6, fontSize: '0.9375rem' }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* How it works */}
        <Box sx={{ py: 6, bgcolor: 'white', borderRadius: 3, px: 4, mb: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: '#0D1F3C',
              mb: 1
            }}>
              이용 방법
            </Typography>
            <Box sx={{ width: 60, height: 2, bgcolor: '#C9A86C', mx: 'auto' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              { step: '1', title: '회원가입', desc: '카카오 간편가입 또는 직접 가입 후 본인 인증을 거쳐 관리자 승인을 받습니다.' },
              { step: '2', title: '카드 받기', desc: '매주 새로운 큐피트 카드를 받고, 하트를 사용해서 상대방 정보를 확인하세요.' },
              { step: '3', title: '대화 시작', desc: '마음에 드는 상대와 자연스럽게 대화를 시작하고 소중한 인연을 만들어 가세요.' },
            ].map((item) => (
              <Box key={item.step} sx={{ flex: '1 1 250px', minWidth: 220, textAlign: 'center' }}>
                <Box sx={{
                  width: 48, height: 48,
                  borderRadius: '50%',
                  bgcolor: '#C9A86C',
                  color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  mx: 'auto', mb: 2,
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 700,
                  fontSize: '1.25rem'
                }}>
                  {item.step}
                </Box>
                <Typography sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 700,
                  fontSize: '1.125rem',
                  color: '#0D1F3C',
                  mb: 1
                }}>
                  {item.title}
                </Typography>
                <Typography sx={{ color: '#6B7A8D', lineHeight: 1.6, fontSize: '0.9375rem' }}>
                  {item.desc}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* CTA Section */}
        <Box sx={{ mb: 8, bgcolor: '#0D1F3C', color: 'white', p: 6, borderRadius: 3, textAlign: 'center' }}>
          <Typography variant="h4" sx={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
            mb: 1
          }}>
            새로운 인연을 시작해보세요
          </Typography>
          <Typography sx={{ fontSize: 18, opacity: 0.9, mb: 4 }}>
            가입 시 7하트 무료 지급!
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => navigate('/register')}
            sx={{
              color: '#0D1F3C',
              px: 5,
              py: 1.5,
              fontSize: '1.125rem',
              borderRadius: 28,
              minHeight: 56,
              fontWeight: 800,
              '&:hover': { bgcolor: '#DEC08A' }
            }}
          >
            무료로 시작하기
          </Button>
        </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MainPage;
